import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, Clock, Award, ChevronLeft, HardHat, Eye, Lock, Compass, Brain, CheckCircle2 } from 'lucide-react';
import { useSovereign } from '../../context/SovereignContext';
import { liveSynth } from '../../core/audio/BinauralEngine';
import { dbStorage } from '../../storage/db';

const Dashboard = () => {
  const navigate = useNavigate();
  const { coins, rank } = useSovereign();
  
  const [totalFocusMinutes, setTotalFocusMinutes] = useState(0);
  const [streakDays, setStreakDays] = useState(0);
  const [weeklyData, setWeeklyData] = useState([]);

  useEffect(() => {
    calculateFocusAnalytics();
  }, []);

  const calculateFocusAnalytics = async () => {
    try {
      const sessions = await dbStorage.getAll('focus_sessions');
      if (!sessions || sessions.length === 0) {
        initDefaultWeekly();
        return;
      }

      // مجموع دقایق تمرکز
      const totalMins = sessions.reduce((acc, s) => acc + (s.durationMinutes || 0), 0);
      setTotalFocusMinutes(totalMins);

      // محاسبه Streak بر اساس روزهای متمایز
      const dates = Array.from(new Set(sessions.map(s => {
        const d = s.completedAt ? new Date(s.completedAt) : new Date();
        return d.toISOString().split('T')[0];
      }))).sort().reverse();

      let streak = 0;
      let checkDate = new Date();
      
      for (const dStr of dates) {
        const d = new Date(dStr);
        const diffDays = Math.floor((checkDate - d) / (1000 * 60 * 60 * 24));
        if (diffDays <= 1) {
          streak++;
          checkDate = d;
        } else {
          break;
        }
      }
      setStreakDays(streak || 1);

      // محاسبه نمودار ۷ روز اخیر
      const daysOfWeek = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
      const last7Days = [];
      const now = new Date();

      for (let i = 6; i >= 0; i--) {
        const target = new Date(now);
        target.setDate(now.getDate() - i);
        const dayStr = target.toISOString().split('T')[0];
        const daySessions = sessions.filter(s => s.completedAt && s.completedAt.startsWith(dayStr));
        const dayMins = daySessions.reduce((sum, s) => sum + (s.durationMinutes || 0), 0);
        
        last7Days.push({
          dayName: daysOfWeek[(target.getDay() + 1) % 7],
          minutes: dayMins
        });
      }

      setWeeklyData(last7Days);
    } catch (e) {
      console.warn('Analytics calculation fallback:', e);
      initDefaultWeekly();
    }
  };

  const initDefaultWeekly = () => {
    const daysOfWeek = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
    setWeeklyData(daysOfWeek.map(d => ({ dayName: d, minutes: 0 })));
  };

  const formatPersian = (num) => String(num).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);

  const maxWeeklyMin = Math.max(...weeklyData.map(d => d.minutes), 60);

  const quickNav = (path) => {
    liveSynth.playClickSfx();
    navigate(path);
  };

  return (
    <div className="space-y-6 pb-24 text-slate-100 max-w-2xl mx-auto text-right select-none" dir="rtl">
      {/* هدر پیشخوان */}
      <div className="bg-slate-900/90 border border-sky-500/30 rounded-3xl p-6 shadow-xl backdrop-blur-md relative overflow-hidden">
        <div className="flex items-center justify-between relative z-10">
          <div>
            <span className="text-[11px] font-bold text-sky-400/80 uppercase tracking-widest">CIVIL NEXUS</span>
            <h1 className="text-xl sm:text-2xl font-black text-white mt-1">پیشخوان مدیریت و تمرکز کارگاهی</h1>
          </div>
          <div className="flex items-center gap-2 bg-sky-500/10 border border-sky-500/30 px-3.5 py-1.5 rounded-2xl">
            <Award className="w-4 h-4 text-sky-400" />
            <span className="text-xs font-black text-sky-400 font-mono">{rank}</span>
          </div>
        </div>

        {/* کارت‌های ۳ گانه متریسی */}
        <div className="grid grid-cols-3 gap-3 mt-6">
          <div className="bg-slate-950/70 border border-slate-800/90 p-3.5 rounded-2xl text-center">
            <div className="flex items-center justify-center gap-1 text-rose-400 mb-1">
              <Flame className="w-4 h-4" />
              <span className="text-[11px] font-medium">پیوستگی</span>
            </div>
            <div className="text-lg font-black text-white font-mono">{formatPersian(streakDays)} <span className="text-[10px] text-slate-400 font-sans">روز</span></div>
          </div>

          <div className="bg-slate-950/70 border border-slate-800/90 p-3.5 rounded-2xl text-center">
            <div className="flex items-center justify-center gap-1 text-sky-400 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-[11px] font-medium">کل تمرکز</span>
            </div>
            <div className="text-lg font-black text-white font-mono">{formatPersian(totalFocusMinutes)} <span className="text-[10px] text-slate-400 font-sans">دقیقه</span></div>
          </div>

          <div className="bg-slate-950/70 border border-slate-800/90 p-3.5 rounded-2xl text-center">
            <div className="flex items-center justify-center gap-1 text-emerald-400 mb-1">
              <Award className="w-4 h-4" />
              <span className="text-[11px] font-medium">امتیاز</span>
            </div>
            <div className="text-lg font-black text-white font-mono">{formatPersian(coins)}</div>
          </div>
        </div>
      </div>

      {/* نمودار هفتگی عملکرد تمرکز */}
      <div className="bg-slate-900/80 border border-slate-800 rounded-3xl p-5 shadow-lg space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-bold text-slate-200 flex items-center gap-2">
            <Brain className="w-4 h-4 text-sky-400" />
            <span>نمودار تمرکز و کار عمیق ۷ روز اخیر</span>
          </h3>
          <span className="text-[10px] text-slate-500">واحد: دقیقه</span>
        </div>

        <div className="flex items-end justify-between gap-2 h-36 pt-6 pb-2 px-2 bg-slate-950/60 rounded-2xl border border-slate-800/80">
          {weeklyData.map((d, i) => {
            const barHeight = Math.max(Math.round((d.minutes / maxWeeklyMin) * 100), 8);
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-2 h-full justify-end">
                <span className="text-[9px] font-mono text-sky-400/80">{d.minutes > 0 ? formatPersian(d.minutes) : ''}</span>
                <div
                  className={`w-full max-w-[28px] rounded-t-lg transition-all duration-700 ${
                    d.minutes > 0 ? 'bg-gradient-to-t from-sky-500 to-amber-300 shadow-md shadow-sky-500/20' : 'bg-slate-800/40'
                  }`}
                  style={{ height: `${barHeight}%` }}
                />
                <span className="text-[10px] font-bold text-slate-400">{d.dayName}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ماژول‌های دسترسی سریع */}
      <div className="space-y-3">
        <h3 className="text-xs font-bold text-slate-400 px-1">دسترسی مستقیم به ماژول‌های عملیاتی</h3>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => quickNav('/focus')}
            className="p-4 bg-slate-900/80 hover:bg-slate-850 border border-slate-800 hover:border-sky-400/40 rounded-2xl flex items-center justify-between text-right transition-all cursor-pointer group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-sky-500/10 rounded-xl text-sky-400 group-hover:scale-110 transition-transform">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-white">اتاق تمرکز</h4>
                <p className="text-[10px] text-slate-400 mt-0.5">پومودورو و صوت بینورال</p>
              </div>
            </div>
            <ChevronLeft className="w-4 h-4 text-slate-600 group-hover:text-sky-400 transition-colors" />
          </button>

          <button
            type="button"
            onClick={() => quickNav('/civil')}
            className="p-4 bg-slate-900/80 hover:bg-slate-850 border border-slate-800 hover:border-sky-400/40 rounded-2xl flex items-center justify-between text-right transition-all cursor-pointer group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-sky-500/10 rounded-xl text-sky-400 group-hover:scale-110 transition-transform">
                <HardHat className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-white">مهندسی عمران</h4>
                <p className="text-[10px] text-slate-400 mt-0.5">صورت‌جلسات و گزارش روزانه</p>
              </div>
            </div>
            <ChevronLeft className="w-4 h-4 text-slate-600 group-hover:text-sky-400 transition-colors" />
          </button>

          <button
            type="button"
            onClick={() => quickNav('/vision')}
            className="p-4 bg-slate-900/80 hover:bg-slate-850 border border-slate-800 hover:border-sky-400/40 rounded-2xl flex items-center justify-between text-right transition-all cursor-pointer group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-sky-500/10 rounded-xl text-sky-400 group-hover:scale-110 transition-transform">
                <Eye className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-white">بینایی ماشین</h4>
                <p className="text-[10px] text-slate-400 mt-0.5">تشخیص ترک و پایش سازه</p>
              </div>
            </div>
            <ChevronLeft className="w-4 h-4 text-slate-600 group-hover:text-sky-400 transition-colors" />
          </button>

          <button
            type="button"
            onClick={() => quickNav('/vault')}
            className="p-4 bg-slate-900/80 hover:bg-slate-850 border border-slate-800 hover:border-sky-400/40 rounded-2xl flex items-center justify-between text-right transition-all cursor-pointer group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-sky-500/10 rounded-xl text-sky-400 group-hover:scale-110 transition-transform">
                <Lock className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-white">گاوصندوق</h4>
                <p className="text-[10px] text-slate-400 mt-0.5">اسناد رمزنگاری AES</p>
              </div>
            </div>
            <ChevronLeft className="w-4 h-4 text-slate-600 group-hover:text-sky-400 transition-colors" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
